import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../APIs/UserContext";
import { getSlotsByDate } from "../APIs/availability";
import { getInsurancePlans } from "../APIs/dropdownApi";
import {
  listPatientInsurances,
  createPatientInsurance,
} from "../APIs/patientInsuranceApi";
import {
  bookAppointmentFromSlot,
  checkAppointmentConflict,
} from "../APIs/appointmentsApi";
import {
  createDepositSession,
  getAcceptingInsurance,
  getProviderPricing,
} from "../APIs/providerApi";
import { showToast } from "../APIs/utils/toastManager";
import AppointmentConflictModal from "./AppointmentConflictModal";
import CompletePatientProfileModal from "./CompletePatientProfileModal";
import { canSubmitBooking } from "./bookingValidation";
import { selectAppointmentSlot } from "../APIs/referralApi";
import keycloak from "../APIs/keycloak";

function extractZone(zdt) {
  const m = String(zdt).match(/\[([^\]]+)\]/);
  return m ? m[1] : "America/New_York";
}

function fmtDate(zdtString, zone) {
  const iso = String(zdtString).split("[")[0];
  return new Date(iso).toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: zone,
  });
}

function fmtTime(zdtString, zone) {
  const iso = String(zdtString).split("[")[0];
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: zone,
  });
}

function sameInstant(a, b) {
  return (
    new Date(String(a).split("[")[0]).getTime() ===
    new Date(String(b).split("[")[0]).getTime()
  );
}

const APPOINTMENT_TYPE_OPTIONS = [
  { value: "NEW_PATIENT", label: "New Patient" },
  { value: "FOLLOW_UP", label: "Follow Up" },
  { value: "TELEHEALTH", label: "Telehealth" },
];

export default function BookingModal({
  open,
  onClose,
  providerId,
  providerName,
  slotZdt,
  specialty,
  facility,
  referralId,
}) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const patientId = currentUser?.patientId;
  const token = keycloak?.token;

  const [step, setStep] = useState(1);

  // Slot info
  const [slotInfo, setSlotInfo] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotError, setSlotError] = useState("");

  // Insurance
  const [insLoading, setInsLoading] = useState(false);
  const [insError, setInsError] = useState("");
  const [plans, setPlans] = useState([]);
  const [useInsurance, setUseInsurance] = useState(true);
  const [planId, setPlanId] = useState("");
  const [planTypeId, setPlanTypeId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [coverageStart, setCoverageStart] = useState("");
  const [coverageEnd, setCoverageEnd] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [appointmentReason, setAppointmentReason] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [pricingRows, setPricingRows] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [acceptingInsurance, setAcceptingInsurance] = useState(null);
  const [insuranceSettingError, setInsuranceSettingError] = useState("");

  // Booking
  const [conflictData, setConflictData] = useState(null);
  const [proceedingAfterConflict, setProceedingAfterConflict] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [depositPending, setDepositPending] = useState(false);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.planId === planId),
    [plans, planId],
  );
  const planTypeOptions = selectedPlan?.planTypes || [];

  const matchedPricing = useMemo(() => {
    const type = appointmentType || slotInfo?.appointmentType;
    if (!type) return null;
    return pricingRows.find((row) => row.appointmentType === type) || null;
  }, [appointmentType, pricingRows, slotInfo?.appointmentType]);

  const canBook = useMemo(() => {
    return canSubmitBooking({
      appointmentReason,
      useInsurance,
      planId,
      planTypeId,
      memberId,
      acceptingInsurance,
      selfPayPricingAvailable: Boolean(matchedPricing),
    });
  }, [
    appointmentReason,
    useInsurance,
    planId,
    planTypeId,
    memberId,
    acceptingInsurance,
    matchedPricing,
  ]);

  // Reset when modal opens with new slot
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSlotInfo(null);
    setSlotError("");
    setAppointmentReason("");
    setAppointmentType("");
    setConflictData(null);
    setSubmitting(false);
    setDepositPending(false);
    setAcceptingInsurance(null);
    setInsuranceSettingError("");
  }, [open, slotZdt]);

  // Fetch slot details when modal opens
  useEffect(() => {
    if (!open || !providerId || !slotZdt) return;
    let cancelled = false;

    async function fetchSlot() {
      setSlotLoading(true);
      setSlotError("");
      try {
        const zone = extractZone(slotZdt);
        const iso = String(slotZdt).split("[")[0];
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: zone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(new Date(iso));
        const year = parts.find((p) => p.type === "year").value;
        const month = parts.find((p) => p.type === "month").value;
        const day = parts.find((p) => p.type === "day").value;
        const date = `${year}-${month}-${day}`;
        const slots = await getSlotsByDate(Number(providerId), { date, zone });
        const match = (slots || []).find((s) =>
          sameInstant(s.slotStart, slotZdt),
        );
        if (!cancelled) {
          if (match) {
            setSlotInfo(match);
            setAppointmentType(match.appointmentType || "");
          } else setSlotError("Could not load slot details.");
        }
      } catch (e) {
        if (!cancelled)
          setSlotError(e?.message || "Failed to load slot details.");
      } finally {
        if (!cancelled) setSlotLoading(false);
      }
    }

    fetchSlot();
    return () => {
      cancelled = true;
    };
  }, [open, providerId, slotZdt]);

  useEffect(() => {
    if (!open || !providerId) return;
    let cancelled = false;

    async function loadPricing() {
      setPricingLoading(true);
      try {
        const [rows, insuranceSetting] = await Promise.all([
          getProviderPricing(Number(providerId)),
          getAcceptingInsurance(Number(providerId)),
        ]);
        if (!cancelled) {
          setPricingRows(Array.isArray(rows) ? rows : []);
          setAcceptingInsurance(insuranceSetting.acceptingInsurance);
          setInsuranceSettingError("");
          if (!insuranceSetting.acceptingInsurance) setUseInsurance(false);
        }
      } catch {
        if (!cancelled) {
          setPricingRows([]);
          setAcceptingInsurance(null);
          setInsuranceSettingError("Unable to load provider payment options.");
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    }

    loadPricing();
    return () => {
      cancelled = true;
    };
  }, [open, providerId]);

  // Load insurance plans + prefill patient's existing insurance
  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;

    async function loadInsurance() {
      setInsLoading(true);
      setInsError("");
      try {
        const planList = await getInsurancePlans();
        if (!cancelled) setPlans(Array.isArray(planList) ? planList : []);

        const all = await listPatientInsurances(patientId);
        const rows = Array.isArray(all) ? all : [];
        if (rows.length > 0 && !cancelled) {
          const pick = rows
            .slice()
            .sort(
              (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
            )[0];
          setPlanId(pick.planId || "");
          setMemberId(pick.memberId || "");
          setGroupId(pick.groupId || "");
          setCoverageStart(pick.coverageStart || "");
          setCoverageEnd(pick.coverageEnd || "");
          setStatus(pick.status || "PENDING");
        }
      } catch (e) {
        if (!cancelled)
          setInsError(e?.message || "Failed to load insurance info.");
      } finally {
        if (!cancelled) setInsLoading(false);
      }
    }

    loadInsurance();
    return () => {
      cancelled = true;
    };
  }, [open, patientId]);

  async function ensureInsuranceForBooking() {
    if (!useInsurance) return null;
    if (memberId?.trim()) {
      const rows = await listPatientInsurances(Number(patientId));
      const exists = (rows || []).some(
        (r) => String(r.memberId).trim() === memberId.trim(),
      );
      if (exists) return memberId.trim();
      if (!planId)
        throw new Error("Please choose an Insurance Plan before booking.");
      const dto = {
        id: null,
        patientId: Number(patientId),
        planId,
        memberId: memberId.trim(),
        groupId: groupId || null,
        coverageStart: coverageStart || null,
        coverageEnd: coverageEnd || null,
        category: null,
        status: status || "PENDING",
        createdAt: null,
        updatedAt: null,
      };
      const created = await createPatientInsurance(Number(patientId), dto);
      return created?.memberId || memberId.trim();
    }
    throw new Error("Member ID is required when paying with insurance.");
  }

  async function finishBooking(slot) {
    const resolvedMemberId = await ensureInsuranceForBooking();
    const bookingPayload = {
      providerId: Number(providerId),
      slotStart: slot.slotStart,
      patientId: Number(patientId),
      appointmentReason: appointmentReason.trim(),
      memberId: resolvedMemberId ?? undefined,
      appointmentType: appointmentType || undefined,
    };
    const booked = referralId
      ? await selectAppointmentSlot(referralId, bookingPayload)
      : await bookAppointmentFromSlot(bookingPayload);

    if (!useInsurance && booked?.appointmentId) {
      const paymentRequired =
        booked.appointmentState === "PAYMENT_PENDING" || !!matchedPricing;
      if (paymentRequired) {
        try {
          setDepositPending(true);
          const { checkoutUrl } = await createDepositSession(
            booked.appointmentId,
          );
          window.location.href = checkoutUrl;
          return;
        } catch (e) {
          showToast(
            "Appointment held, but payment could not be started. Please try again.",
            "error",
          );
          throw e;
        }
      }
    }

    const bookedDate = slot.slotStart.split("T")[0];
    showToast("Appointment booked successfully!", "success");
    onClose();
    navigate(
      `/PatientProfile?tab=appointments&date=${encodeURIComponent(bookedDate)}`,
    );
  }

  async function handleBookClick() {
    if (!currentUser) return;
    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }
    if (!slotInfo) return;

    setSubmitting(true);
    try {
      if (slotInfo.status !== "FREE") {
        showToast(
          `That slot is ${slotInfo.status}. Please choose another time.`,
          "error",
        );
        return;
      }
      const conflicts = await checkAppointmentConflict(
        patientId,
        slotInfo.slotStart,
        slotInfo.slotEnd,
      );
      if (conflicts.length > 0) {
        setConflictData({ conflicts, pendingSlot: slotInfo });
        return;
      }
      await finishBooking(slotInfo);
    } catch (e) {
      showToast(e?.message || "Booking failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">
              Book Appointment with {providerName}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center px-6 py-4">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= 1
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {step > 1 ? "✓" : "1"}
              </div>
              <span
                className={`text-sm font-medium ${step === 1 ? "text-gray-900" : "text-gray-400"}`}
              >
                Confirm Time
              </span>
            </div>
            <div
              className={`flex-1 h-px mx-3 ${step >= 2 ? "bg-teal-400" : "bg-gray-200"}`}
            />
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step === 2
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                2
              </div>
              <span
                className={`text-sm font-medium ${step === 2 ? "text-gray-900" : "text-gray-400"}`}
              >
                Insurance &amp; Reason
              </span>
            </div>
          </div>

          {/* Step 1 — Confirm Time */}
          {step === 1 && (
            <div className="px-6 pb-6">
              {slotLoading && (
                <p className="text-gray-500 text-sm">Loading slot details...</p>
              )}
              {slotError && <p className="text-red-600 text-sm">{slotError}</p>}
              {slotInfo && !slotLoading && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Date
                      </p>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        {fmtDate(slotInfo.slotStart, extractZone(slotZdt))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Telehealth
                      </p>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        {slotInfo.telehealth ? "Yes" : "No"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Start Time
                      </p>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        {fmtTime(slotInfo.slotStart, extractZone(slotZdt))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        End Time
                      </p>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        {fmtTime(slotInfo.slotEnd, extractZone(slotZdt))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Status
                      </p>
                      <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                        {slotInfo.status}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Insurance & Reason */}
          {step === 2 && (
            <div className="px-6 pb-6 space-y-5">
              {/* Insurance */}
              <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    Insurance
                  </h3>
                  {insLoading && (
                    <span className="text-xs text-gray-400">Loading...</span>
                  )}
                  {insError && (
                    <span className="text-xs text-red-600">{insError}</span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setUseInsurance(true)}
                    disabled={acceptingInsurance !== true}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      useInsurance
                        ? "bg-teal-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    }`}
                  >
                    Pay with Insurance
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseInsurance(false)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      !useInsurance
                        ? "bg-teal-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Pay without Insurance
                  </button>
                </div>

                {acceptingInsurance === false && (
                  <p className="text-sm text-amber-700">
                    Provider accepts self-pay only.
                  </p>
                )}
                {insuranceSettingError && (
                  <p className="text-sm text-red-600">{insuranceSettingError}</p>
                )}

                {!useInsurance &&
                  (() => {
                    if (pricingLoading) {
                      return (
                        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          Loading self-pay pricing...
                        </p>
                      );
                    }

                    if (matchedPricing) {
                      const fullPrice = Number(matchedPricing.fullPrice);
                      const depositAmount = Number(
                        matchedPricing.depositAmount,
                      );
                      const remainingAmount = Math.max(
                        0,
                        fullPrice - depositAmount,
                      );

                      return (
                        <div className="rounded-lg bg-teal-50 border border-teal-200 px-3 py-3 text-sm space-y-2">
                          <p className="font-semibold text-teal-800">
                            Self-Pay Pricing
                          </p>
                          <div className="flex justify-between text-teal-700">
                            <span>Full appointment cost</span>
                            <span>${fullPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-teal-700 font-medium">
                            <span>Deposit due now via Stripe</span>
                            <span>${depositAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-teal-600">
                            <span>Remaining due at visit</span>
                            <span>${remainingAmount.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    }

                    return acceptingInsurance === false ? (
                      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        Self-pay pricing is unavailable for this appointment type.
                      </p>
                    ) : (
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        Cost will be out of pocket.
                      </p>
                    );
                  })()}

                {useInsurance && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance Plan <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={planId}
                        onChange={(e) => {
                          setPlanId(e.target.value);
                          setPlanTypeId("");
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">Select a plan…</option>
                        {plans.map((p) => (
                          <option key={p.planId} value={p.planId}>
                            {p.planName} ({p.planId})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={planTypeId}
                        onChange={(e) => setPlanTypeId(e.target.value)}
                        disabled={!planId || planTypeOptions.length === 0}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                      >
                        <option value="">Select type…</option>
                        {planTypeOptions.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.typeName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Member ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={memberId}
                        onChange={(e) => setMemberId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., MEM-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Group ID (optional)
                      </label>
                      <input
                        type="text"
                        value={groupId}
                        onChange={(e) => setGroupId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., GRP-123"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Coverage Start
                      </label>
                      <input
                        type="date"
                        value={coverageStart}
                        onChange={(e) => setCoverageStart(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Coverage End
                      </label>
                      <input
                        type="date"
                        value={coverageEnd}
                        onChange={(e) => setCoverageEnd(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="PENDING">PENDING</option>
                        <option value="VERIFIED">VERIFIED</option>
                        <option value="REJECTED">REJECTED</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                <h3 className="text-base font-semibold text-gray-900">
                  Appointment Type
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {APPOINTMENT_TYPE_OPTIONS.map((type) => {
                    const pricing = pricingRows.find(
                      (row) => row.appointmentType === type.value,
                    );
                    const selected = appointmentType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setAppointmentType(type.value)}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                          selected
                            ? "border-teal-600 bg-teal-50 text-teal-800"
                            : "border-gray-200 bg-white text-gray-700 hover:border-teal-300"
                        }`}
                      >
                        <span className="block font-semibold">
                          {type.label}
                        </span>
                        {pricing ? (
                          <span className="block text-xs mt-1">
                            ${Number(pricing.depositAmount).toFixed(2)} due now
                          </span>
                        ) : (
                          <span className="block text-xs mt-1 text-gray-400">
                            No set price
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Appointment Reason */}
              <div className="rounded-xl border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Appointment Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={appointmentReason}
                  onChange={(e) => setAppointmentReason(e.target.value)}
                  placeholder="e.g. Follow-up on MRI"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={step === 1 ? onClose : () => setStep(1)}
              className="text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              {step === 1 ? "Cancel" : "← Back"}
            </button>

            {step === 1 ? (
              <button
                type="button"
                disabled={slotLoading || !!slotError || !slotInfo}
                onClick={() => setStep(2)}
                className="px-5 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue →
              </button>
            ) : (
              <button
                type="button"
                disabled={!canBook || submitting || depositPending}
                onClick={handleBookClick}
                className="px-5 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {depositPending
                  ? "Redirecting to payment..."
                  : submitting
                    ? "Booking..."
                    : !useInsurance && matchedPricing
                      ? "Pay Deposit & Book"
                      : "Book Appointment"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Conflict modal */}
      {conflictData && (
        <AppointmentConflictModal
          conflicts={conflictData.conflicts}
          patientId={Number(patientId)}
          proceeding={proceedingAfterConflict}
          onGoBack={() => setConflictData(null)}
          onCancelAndProceed={async () => {
            setProceedingAfterConflict(true);
            try {
              await finishBooking(conflictData.pendingSlot);
            } finally {
              setProceedingAfterConflict(false);
              setConflictData(null);
            }
          }}
        />
      )}

      {/* Complete profile modal */}
      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={() => {
          setShowCompleteProfileModal(false);
        }}
      />
    </>
  );
}
